// src/components/shared/ImageAttachment.tsx
// ============================================================================
// COMPONENTE: Adjunto de Imagen para WhatsApp
// ============================================================================
//
// Permite subir una imagen, comprimirla en el browser con Canvas API y
// devolverla como base64 (con prefijo data:image/jpeg;base64,...).
// No realiza ninguna petición a servidor — todo ocurre en cliente.
//
// v1.1.0: Lightbox — clic en thumbnail abre modal de preview a tamaño completo.
//   - Portal renderizado en document.body (no afectado por z-index del panel)
//   - Backdrop oscuro con backdrop-blur
//   - Clic fuera o en X cierra el modal
//   - Overlay con icono Expand al hacer hover sobre thumbnail
//
// Props:
//   imageBase64     - Valor actual (null = sin imagen)
//   onImageChange   - Callback: recibe base64 con prefijo o null al quitar
//   label           - Etiqueta del campo (default: "Imagen adjunta")
//   helpText        - Texto de ayuda debajo del label
//   maxWidth        - Ancho máximo de compresión (default: 800)
//   maxHeight       - Alto máximo de compresión (default: 800)
//   quality         - Calidad JPEG 0-1 (default: 0.7)
//   maxInputSizeMB  - Límite de tamaño del archivo de entrada (default: 5)
//   disabled        - Desactiva el componente
//   className       - Clases adicionales para el contenedor
// ============================================================================

import React, { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ImagePlus, X, RefreshCw, Loader2, AlertCircle, ImageIcon, Expand } from 'lucide-react';

// ============================================================================
// INTERFACES
// ============================================================================

interface ImageAttachmentProps {
  imageBase64: string | null;
  onImageChange: (base64: string | null) => void;
  label?: string;
  helpText?: string;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxInputSizeMB?: number;
  disabled?: boolean;
  className?: string;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.webp';

// ============================================================================
// HELPER: Comprimir imagen con Canvas API
// ============================================================================

async function compressImage(
  file: File,
  maxWidth: number,
  maxHeight: number,
  quality: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No se pudo inicializar el canvas'));
          return;
        }

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        resolve(canvas.toDataURL('image/jpeg', quality));
      };

      img.onerror = () => reject(new Error('No se pudo leer la imagen. Verifica que el archivo sea válido.'));
      img.src = dataUrl;
    };

    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsDataURL(file);
  });
}

function estimateSizeKB(base64: string): number {
  const data = base64.includes(',') ? base64.split(',')[1] : base64;
  return Math.round((data.length * 0.75) / 1024);
}

// ============================================================================
// COMPONENTE
// ============================================================================

const ImageAttachment: React.FC<ImageAttachmentProps> = ({
  imageBase64,
  onImageChange,
  label = 'Imagen adjunta',
  helpText,
  maxWidth = 800,
  maxHeight = 800,
  quality = 0.7,
  maxInputSizeMB = 5,
  disabled = false,
  className = '',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false); // ← v1.1.0

  // ── Procesar archivo ───────────────────────────────────────────────────────

  const handleFile = useCallback(async (file: File) => {
    setError(null);

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Formato no válido. Usa JPEG, PNG o WebP.');
      return;
    }

    if (file.size > maxInputSizeMB * 1024 * 1024) {
      setError(`El archivo supera los ${maxInputSizeMB}MB permitidos.`);
      return;
    }

    setIsCompressing(true);
    try {
      const compressed = await compressImage(file, maxWidth, maxHeight, quality);
      onImageChange(compressed);
    } catch (err: any) {
      setError(err.message || 'Error al procesar la imagen');
    } finally {
      setIsCompressing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [maxWidth, maxHeight, quality, maxInputSizeMB, onImageChange]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleRemove = useCallback(() => {
    setError(null);
    setShowPreview(false);
    onImageChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onImageChange]);

  const handleClickZone = useCallback(() => {
    if (!disabled && !isCompressing) fileInputRef.current?.click();
  }, [disabled, isCompressing]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (disabled || isCompressing) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [disabled, isCompressing, handleFile]);

  // Solo estimamos tamaño para data-URI base64; si el valor es una URL
  // (imagen ya hospedada en el asset service) no aplica.
  const sizeKB = imageBase64 && imageBase64.startsWith('data:') ? estimateSizeKB(imageBase64) : 0;

  // ============================================================================
  // RENDER: Lightbox modal (v1.1.0)
  // ============================================================================
  //
  // Portal en document.body para no verse afectado por el z-index del panel.
  // La imagen se muestra a su tamaño real (máximo 80vh de alto).
  //
  const renderLightbox = () => {
    if (!showPreview || !imageBase64) return null;

    return createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Vista previa de imagen"
      >
        {/* Backdrop — clic cierra */}
        <div
          className="modal-overlay fixed inset-0 bg-black/75 backdrop-blur-sm"
          onClick={() => setShowPreview(false)}
        />

        {/* Contenedor imagen */}
        <div className="modal-panel relative z-10 w-full max-w-2xl mx-auto">
          {/* Botón cerrar */}
          <button
            type="button"
            onClick={() => setShowPreview(false)}
            className="absolute -top-3 -right-3 z-20 bg-surface rounded-full p-1.5 shadow-lg hover:bg-canvas transition-colors"
            aria-label="Cerrar preview"
          >
            <X className="h-4 w-4 text-content-2" />
          </button>

          {/* Imagen */}
          <div className="rounded-xl overflow-hidden shadow-2xl bg-surface">
            <img
              src={imageBase64}
              alt="Vista previa completa"
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          </div>

          {/* Meta info */}
          <div className="mt-2 flex items-center justify-center gap-3">
            {sizeKB > 0 && <span className="text-xs text-white/60">~{sizeKB} KB</span>}
            {sizeKB > 0 && <span className="text-white/30">·</span>}
            <span className="text-xs text-white/60">
              Comprimida a máx. {maxWidth}×{maxHeight}px JPEG
            </span>
            <span className="text-white/30">·</span>
            <span className="text-xs text-white/60">Clic fuera para cerrar</span>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // ── Render principal ───────────────────────────────────────────────────────

  return (
    <div className={`space-y-2 ${className}`}>

      {/* Label */}
      <div>
        <p className="text-sm font-medium text-content-2 flex items-center gap-1.5">
          <ImageIcon className="h-4 w-4 text-content-3" />
          {label}
        </p>
        {helpText && (
          <p className="text-xs text-content-3 mt-0.5 ml-5">{helpText}</p>
        )}
      </div>

      {/* Input oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        onChange={handleInputChange}
        disabled={disabled || isCompressing}
        className="sr-only"
        aria-label="Seleccionar imagen"
      />

      {/* Sin imagen */}
      {!imageBase64 && !isCompressing && (
        <div
          onClick={handleClickZone}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => e.key === 'Enter' && handleClickZone()}
          aria-label="Subir imagen"
          className={[
            'relative border-2 border-dashed rounded-lg p-5 text-center transition-colors',
            disabled
              ? 'border-line bg-surface-2 cursor-not-allowed'
              : 'border-line bg-surface hover:border-pink-300 hover:bg-pink-50 dark:hover:bg-pink-500/10 cursor-pointer',
          ].join(' ')}
        >
          <div className="flex flex-col items-center gap-2">
            <div className={`p-2.5 rounded-full ${disabled ? 'bg-canvas' : 'bg-pink-50 dark:bg-pink-500/10'}`}>
              <ImagePlus className={`h-5 w-5 ${disabled ? 'text-content-3' : 'text-pink-400'}`} />
            </div>
            <div>
              <p className={`text-sm font-medium ${disabled ? 'text-content-3' : 'text-content-2'}`}>
                {disabled ? 'No disponible' : 'Seleccionar imagen'}
              </p>
              {!disabled && (
                <p className="text-xs text-content-3 mt-0.5">
                  JPEG, PNG o WebP · Máx. {maxInputSizeMB}MB · Se comprime automáticamente
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comprimiendo */}
      {isCompressing && (
        <div className="border-2 border-dashed border-pink-200 rounded-lg p-5 bg-pink-50 dark:bg-pink-500/10">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-5 w-5 text-pink-400 animate-spin" />
            <p className="text-sm text-content-2">Comprimiendo imagen...</p>
            <p className="text-xs text-content-3">Esto solo toma un momento</p>
          </div>
        </div>
      )}

      {/* Imagen cargada */}
      {imageBase64 && !isCompressing && (
        <div className="border border-line rounded-lg bg-surface overflow-hidden">
          <div className="flex items-center gap-3 p-3">

            {/* ── Thumbnail clickeable — abre lightbox ── */}
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="flex-shrink-0 h-16 w-16 rounded-md overflow-hidden border border-line bg-surface-2 relative group cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-1"
              title="Clic para ver a tamaño completo"
              aria-label="Ver imagen completa"
            >
              <img
                src={imageBase64}
                alt="Vista previa"
                className="h-full w-full object-cover transition-opacity group-hover:opacity-75"
              />
              {/* Overlay hover */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/25">
                <Expand className="h-4 w-4 text-white drop-shadow-md" />
              </div>
            </button>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-content">Imagen lista</p>
              {sizeKB > 0 && (
                <p className="text-xs text-content-3 mt-0.5">
                  Tamaño aprox.: <span className="font-medium text-content-2">{sizeKB} KB</span>
                  {sizeKB > 900 && (
                    <span className="ml-1.5 text-amber-600 font-medium">· cerca del límite</span>
                  )}
                </p>
              )}
              <p className="text-xs text-emerald-600 mt-0.5">
                ✓ Se enviará como imagen en los mensajes de WhatsApp
              </p>
              <p className="text-xs text-content-3 mt-0.5">
                Toca la miniatura para ver completa
              </p>
            </div>

            {/* Acciones */}
            {!disabled && (
              <div className="flex-shrink-0 flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={handleClickZone}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md text-content-2 bg-canvas hover:bg-surface-2 transition-colors"
                  title="Cambiar imagen"
                >
                  <RefreshCw className="h-3 w-3" />
                  Cambiar
                </button>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
                  title="Quitar imagen"
                >
                  <X className="h-3 w-3" />
                  Quitar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-500/10 border border-red-100 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Lightbox portal — v1.1.0 */}
      {renderLightbox()}
    </div>
  );
};

export default ImageAttachment;